import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  apiErrorText,
  useGetPtoAosrRequisitesQuery,
  useSetPtoAosrRequisitesMutation,
  useListPtoExecutiveActsQuery,
  useDeletePtoExecutiveActMutation,
  useRestorePtoExecutiveActMutation,
  useGeneratePtoActMutation,
  useSetPtoActStatusMutation,
  useLazyExportPtoActDocxQuery,
  useListPtoOrderRepsQuery,
  useCreatePtoOrderRepMutation,
  useDeletePtoOrderRepMutation,
  useListPtoQualityMaterialsQuery,
  useCreatePtoQualityMaterialMutation,
  useDeletePtoQualityMaterialMutation,
  useSyncPtoQualityMaterialsFromRdMutation,
  useListPtoActTemplatesQuery,
  useUploadPtoActTemplateMutation,
  useDeletePtoActTemplateMutation,
  useListPtoRequestsQuery,
  useGenerateFieldInspectionActMutation,
  useListPtoIdPackagesQuery,
  useCreatePtoIdPackageMutation,
  useSetPtoIdPackageStatusMutation,
  type PtoExecutiveAct,
  type PtoOrderRepresentative,
  type PtoQualityMaterial,
  type PtoActTemplate,
  type PtoIdPackage,
} from '@shared/api';
import { useAgentPermission, usePtoDeleteAccess } from '@entities/user';
import {
  aosrFormFromRequisites,
  aosrFormsEqual,
  aosrHeaderFromLocal,
  aosrRepDetails,
  aosrRepFilled,
  aosrRequisitesBody,
  emptyAosrHeader,
  type AosrHeaderForm,
  type AosrHeaderKey,
  type AosrRepForm,
} from '@entities/field';
import { Button } from '@shared/ui/Button';
import { deletionDaysLeft, DeletionBadge } from './deletion';
import { Input } from '@shared/ui/Input';

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(360px, 460px) 1fr;
  gap: ${({ theme }) => theme.spacing(5)};

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(4)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Select = styled.select`
  padding: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
`;

const TextArea = styled.textarea`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  font: inherit;
  min-height: 60px;
  resize: vertical;
`;

const Details = styled.details`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};

  & > summary {
    cursor: pointer;
    font-weight: 600;
    font-size: 14px;
  }
`;

const PosItem = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  padding: ${({ theme }) => theme.spacing(1)} 0;
`;

const ActRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2.5)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

/** Полоса состояния реквизитов проекта (сохранены / не сохранены / перенос). */
const ReqsBar = styled.div<{ $tone: 'ok' | 'warn' | 'error' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  line-height: 1.5;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid
    ${({ theme, $tone }) =>
      $tone === 'error'
        ? theme.colors.status.danger
        : $tone === 'warn'
          ? theme.colors.status.warning
          : theme.colors.border};
  color: ${({ theme }) => theme.colors.text.primary};

  & > div:first-child {
    flex: 1 1 260px;
  }
`;

const RepCard = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const statusLabel: Record<string, string> = {
  draft: 'черновик',
  on_signing: 'на подписи',
  signed: 'подписан',
  submitted: 'сдан',
};

const kindLabel: Record<string, string> = {
  aosr: 'АОСР',
  aosk: 'АОСК',
  aousito: 'АОУСИТО',
};

const REP_ROLES: Array<{ value: string; label: string }> = [
  { value: 'developer_control', label: 'Застройщик/заказчик — стройконтроль' },
  { value: 'builder', label: 'Лицо, осуществляющее строительство' },
  { value: 'builder_control', label: 'Строитель — стройконтроль' },
  { value: 'designer', label: 'Проектировщик' },
  { value: 'performer', label: 'Выполнивший работы (субподряд)' },
  { value: 'other', label: 'Иные представители' },
];

function repRoleLabel(role: string): string {
  return REP_ROLES.find((x) => x.value === role)?.label ?? role;
}

const DEFAULT_FORM_REF = 'Приложение № 3 к приказу Минстроя России от 16.05.2023 № 344/пр';

/** Реквизиты АО «САТКОМ» как лица, осуществляющего строительство (стабильны —
 *  предзаполняются по умолчанию, редактируемы). */
const SATKOM_BUILDER =
  'АО «САТКОМ», ОГРН 1027201301973, ИНН 7206010213, 108818, г. Москва, вн. тер. г. ' +
  'городской округ Троицк, ул. Кварцевая, д. 4 к. 3, помещ. 2Н, тел/факс: 8-800-301-28-28, ' +
  'Ассоциация СРО «ЭкспертСтрой», 107078, г. Москва, пер. Орликова, д. 4, этаж 2, пом. 1, ' +
  'ком. 6, ОГРН 1127799010668, ИНН 7708240612';

const SATKOM_PERFORMER = 'АО «САТКОМ»';

/** Представители АО «САТКОМ» по умолчанию (предзаполнение, пока у проекта нет
 *  своих реквизитов; редактируемо). В акте — «должность ФИО, документ». */
const SATKOM_DEFAULT_REPS: AosrRepForm[] = [
  {
    role: 'builder',
    position: 'Заместитель генерального директора по строительству',
    name: 'Мужиков К.О.',
    order_doc: 'приказ 215/2-П от 07.10.2024 г., АО «САТКОМ»',
  },
  {
    role: 'builder_control',
    position: 'Операционный директор',
    name: 'Лой А.А.',
    order_doc: 'С-74-273928, приказ 215/2-П от 07.10.2024 г., АО «САТКОМ»',
  },
];

/** Шапка по умолчанию, пока у проекта нет своих реквизитов: форма 344/пр и
 *  АО «САТКОМ» как лицо, осуществляющее строительство. */
function defaultHeader(): AosrHeaderForm {
  return { ...emptyAosrHeader(), form_ref: DEFAULT_FORM_REF, builder: SATKOM_BUILDER };
}

function defaultReps(): AosrRepForm[] {
  return SATKOM_DEFAULT_REPS.map((r) => ({ ...r }));
}

/** Состояние реквизитов проекта для полосы над формой. */
function requisitesStatus(opts: {
  loading: boolean;
  error: string | null;
  saved: boolean;
  dirty: boolean;
}): { tone: 'ok' | 'warn' | 'error'; text: string } {
  if (opts.loading) return { tone: 'ok', text: 'Реквизиты проекта загружаются…' };
  if (opts.error) {
    return { tone: 'error', text: `Реквизиты проекта не загрузились: ${opts.error}` };
  }
  if (opts.dirty) {
    return {
      tone: 'warn',
      text: opts.saved
        ? 'Реквизиты изменены, но не сохранены: этот акт соберётся с ними, а АОСР по заявкам Прораба — с сохранёнными.'
        : 'Реквизиты проекта не сохранены: этот акт соберётся с реквизитами формы, а в АОСР по заявкам Прораба стороны и объект возьмутся из Договора, ОЖР и штампов РД.',
    };
  }
  return opts.saved
    ? {
        tone: 'ok',
        text: 'Единый паспорт реквизитов проекта сохранён (синхронизирован с ОЖР и АОСР по заявкам Прораба).',
      }
    : {
        tone: 'warn',
        text: 'Реквизиты проекта ещё не закреплены: нажмите «Сохранить реквизиты проекта» или заполните их из Договора и РД.',
      };
}

export function PtoActs() {
  const { projectId = '' } = useParams();
  const { can_write } = useAgentPermission('pto');
  const canDelete = usePtoDeleteAccess();
  const [showDeleted, setShowDeleted] = useState(false);

  const { data: actsData, isLoading: actsLoading } = useListPtoExecutiveActsQuery(
    { projectId, deleted: showDeleted },
    { skip: !projectId },
  );
  const [generate, { isLoading: generating }] = useGeneratePtoActMutation();
  const [setStatus] = useSetPtoActStatusMutation();
  const [deleteAct] = useDeletePtoExecutiveActMutation();
  const [restoreAct] = useRestorePtoExecutiveActMutation();
  const [exportDocx] = useLazyExportPtoActDocxQuery();

  const [kind, setKind] = useState('aosr');
  const [actNo, setActNo] = useState('');
  const [actDate, setActDate] = useState('');
  const [workName, setWorkName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const legacyKey = `pto-act-reqs-${projectId}`;
  const reqQ = useGetPtoAosrRequisitesQuery(projectId, { skip: !projectId });
  const [saveReqs, { isLoading: savingReqs }] = useSetPtoAosrRequisitesMutation();
  const serverReqs = reqQ.currentData?.requisites ?? null;
  const fallbackReqs = reqQ.currentData?.fallback ?? null;
  const [header, setHeader] = useState<AosrHeaderForm>(defaultHeader);
  const [reps, setReps] = useState<AosrRepForm[]>(defaultReps);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [legacy, setLegacy] = useState<AosrHeaderForm | null>(null);
  const [reqsNote, setReqsNote] = useState<{ ok: boolean; text: string } | null>(null);
  const { data: repsData } = useListPtoOrderRepsQuery(projectId, { skip: !projectId });
  const [createRep] = useCreatePtoOrderRepMutation();
  const [deleteRep] = useDeletePtoOrderRepMutation();
  const registryReps: PtoOrderRepresentative[] = repsData?.items ?? [];
  const [selectedRepIds, setSelectedRepIds] = useState<Record<string, boolean>>({});
  const [nrRole, setNrRole] = useState('builder');
  const [nrPosition, setNrPosition] = useState('');
  const [nrFullName, setNrFullName] = useState('');
  const [nrNrs, setNrNrs] = useState('');
  const [nrOrder, setNrOrder] = useState('');
  const [nrOrg, setNrOrg] = useState('');
  const { data: matsData } = useListPtoQualityMaterialsQuery(projectId, { skip: !projectId });
  const [createMat] = useCreatePtoQualityMaterialMutation();
  const [deleteMat] = useDeletePtoQualityMaterialMutation();
  const [syncMatsFromRd, { isLoading: syncingMats }] = useSyncPtoQualityMaterialsFromRdMutation();
  const [syncMatsNote, setSyncMatsNote] = useState<string | null>(null);
  const registryMaterials: PtoQualityMaterial[] = matsData?.items ?? [];
  const [selectedMatIds, setSelectedMatIds] = useState<Record<string, boolean>>({});
  const [nmName, setNmName] = useState('');
  const [nmDocs, setNmDocs] = useState('');
  const { data: tplData } = useListPtoActTemplatesQuery(projectId, { skip: !projectId });
  const [uploadTpl, { isLoading: uploadingTpl }] = useUploadPtoActTemplateMutation();
  const [deleteTpl] = useDeletePtoActTemplateMutation();
  const actTemplates: PtoActTemplate[] = tplData?.items ?? [];
  const [templateId, setTemplateId] = useState('');
  const [tplName, setTplName] = useState('');
  const [tplFile, setTplFile] = useState<File | null>(null);
  const [performer, setPerformer] = useState(SATKOM_PERFORMER);
  const [rdDocs, setRdDocs] = useState('');
  const [materialsText, setMaterialsText] = useState('');
  const [materialsRegistryRef, setMaterialsRegistryRef] = useState('');
  const [complianceDocs, setComplianceDocs] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [norms, setNorms] = useState('');
  const [nextWorks, setNextWorks] = useState('');
  const [appendices, setAppendices] = useState('');

  // Заявки со стройки на освидетельствование (Прораб -> АОСР в 1 клик)
  const { data: requestsData } = useListPtoRequestsQuery(projectId, { skip: !projectId });
  const [generateFieldAct, { isLoading: generatingFieldAct }] =
    useGenerateFieldInspectionActMutation();
  const [fieldActNote, setFieldActNote] = useState<string | null>(null);
  const inspectionRequests = requestsData?.inspection_requests ?? [];

  // Комплекты ИД для сдачи в СТДО прямо на экране актов
  const { data: pkgData } = useListPtoIdPackagesQuery(projectId, { skip: !projectId });
  const [createPkg, { isLoading: savingPkg }] = useCreatePtoIdPackageMutation();
  const [setPkgStatus] = useSetPtoIdPackageStatusMutation();
  const packages: PtoIdPackage[] = pkgData?.items ?? [];
  const [pkgTitle, setPkgTitle] = useState('');
  const [pkgActIds, setPkgActIds] = useState<Record<string, boolean>>({});
  const [stdoRefs, setStdoRefs] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!projectId || loadedFor === projectId) return;
    if (reqQ.isFetching || (!reqQ.isSuccess && !reqQ.isError)) return;
    const fromServer = reqQ.isSuccess ? aosrFormFromRequisites(serverReqs) : null;
    if (fromServer) {
      const h = { ...fromServer.header };
      if (fallbackReqs) {
        if (!h.object_name.trim() && fallbackReqs.object_name) h.object_name = fallbackReqs.object_name;
        if (!h.object_address.trim() && fallbackReqs.object_address) h.object_address = fallbackReqs.object_address;
        if (!h.developer.trim() && fallbackReqs.developer) h.developer = fallbackReqs.developer;
        if (!h.builder.trim() && fallbackReqs.builder) h.builder = fallbackReqs.builder;
        if (!h.designer.trim() && fallbackReqs.designer) h.designer = fallbackReqs.designer;
      }
      setHeader(h);
      setReps(fromServer.reps);
      setLegacy(null);
    } else {
      let local: AosrHeaderForm | null = null;
      try {
        local = aosrHeaderFromLocal(localStorage.getItem(legacyKey));
      } catch {
        local = null;
      }
      const base = local ?? defaultHeader();
      if (!local && fallbackReqs) {
        if (!base.object_name.trim() && fallbackReqs.object_name) base.object_name = fallbackReqs.object_name;
        if (!base.object_address.trim() && fallbackReqs.object_address) base.object_address = fallbackReqs.object_address;
        if (!base.developer.trim() && fallbackReqs.developer) base.developer = fallbackReqs.developer;
        if (!base.builder.trim() && fallbackReqs.builder) base.builder = fallbackReqs.builder;
        if (!base.designer.trim() && fallbackReqs.designer) base.designer = fallbackReqs.designer;
      }
      setHeader(base);
      setReps(defaultReps());
      setLegacy(local);
    }
    setReqsNote(null);
    setLoadedFor(projectId);
  }, [projectId, loadedFor, reqQ.isFetching, reqQ.isSuccess, reqQ.isError, serverReqs, fallbackReqs, legacyKey]);

  const savedForm = aosrFormFromRequisites(serverReqs);
  const reqsDirty =
    loadedFor === projectId &&
    !aosrFormsEqual({ header, reps }, savedForm ?? { header: emptyAosrHeader(), reps: [] });
  const reqsStatus = requisitesStatus({
    loading: reqQ.isLoading || loadedFor !== projectId,
    error: reqQ.isError ? apiErrorText(reqQ.error, 'ошибка сервера') : null,
    saved: !!savedForm,
    dirty: reqsDirty,
  });

  function setHeaderField(key: AosrHeaderKey, value: string) {
    setHeader((prev) => ({ ...prev, [key]: value }));
  }

  function onAutofillFromFallback() {
    if (!fallbackReqs) return;
    setHeader((prev) => ({
      ...prev,
      object_name: prev.object_name.trim() || fallbackReqs.object_name || prev.object_name,
      object_address: prev.object_address.trim() || fallbackReqs.object_address || prev.object_address,
      developer: prev.developer.trim() || fallbackReqs.developer || prev.developer,
      builder: prev.builder.trim() || fallbackReqs.builder || prev.builder,
      designer: prev.designer.trim() || fallbackReqs.designer || prev.designer,
    }));
    setReqsNote({
      ok: true,
      text: 'Подставлены реквизиты из Договора (Статут), ОЖР и штампов РД. Нажмите «Сохранить реквизиты проекта», чтобы закрепить их в едином паспорте объекта.',
    });
  }

  /** Сохранить реквизиты проекта на сервере (в том числе перенос копии браузера). */
  async function onSaveReqs() {
    setReqsNote(null);
    try {
      await saveReqs({
        projectId,
        requisites: aosrRequisitesBody({ header, reps }),
      }).unwrap();
      if (legacy) {
        // Перенесено — копия браузера больше не нужна (и не читается).
        try {
          localStorage.removeItem(legacyKey);
        } catch {
          /* хранилище недоступно — копия просто останется */
        }
        setLegacy(null);
        setReqsNote({
          ok: true,
          text: 'Реквизиты перенесены в проект; копия в этом браузере удалена.',
        });
      }
      // Иначе итог виден в полосе состояния («сохранены»).
    } catch (e) {
      setReqsNote({ ok: false, text: apiErrorText(e, 'Не удалось сохранить реквизиты проекта.') });
    }
  }

  /** Не переносить копию браузера: удалить её, форма — к умолчаниям. */
  function onDropLegacy() {
    try {
      localStorage.removeItem(legacyKey);
    } catch {
      /* хранилище недоступно */
    }
    setLegacy(null);
    setHeader(defaultHeader());
  }

  const acts: PtoExecutiveAct[] = actsData?.items ?? [];

  function addRep() {
    setReps((prev) => [...prev, { role: 'builder', position: '', name: '', order_doc: '' }]);
  }
  function updateRep(i: number, patch: Partial<AosrRepForm>) {
    setReps((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function removeRep(i: number) {
    setReps((prev) => prev.filter((_, idx) => idx !== i));
  }

  function parseMaterials() {
    return materialsText
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0)
      .map((l) => {
        const [name, quality] = l.split('|').map((s) => s.trim());
        return { name, quality_docs: quality || undefined };
      });
  }

  async function onAddRegistryRep() {
    if (!nrFullName.trim()) return;
    try {
      await createRep({
        projectId,
        role: nrRole,
        position: nrPosition.trim() || undefined,
        full_name: nrFullName.trim(),
        nrs: nrNrs.trim() || undefined,
        order_ref: nrOrder.trim() || undefined,
        organization: nrOrg.trim() || undefined,
      }).unwrap();
      setNrPosition('');
      setNrFullName('');
      setNrNrs('');
      setNrOrder('');
      setNrOrg('');
    } catch {
      setError('Не удалось добавить представителя в реестр.');
    }
  }

  async function onAddRegistryMat() {
    if (!nmName.trim()) return;
    try {
      await createMat({
        projectId,
        name: nmName.trim(),
        quality_docs: nmDocs.trim() || undefined,
      }).unwrap();
      setNmName('');
      setNmDocs('');
    } catch {
      setError('Не удалось добавить материал в реестр.');
    }
  }

  async function onSyncMaterialsFromRd() {
    setSyncMatsNote(null);
    try {
      const res = await syncMatsFromRd(projectId).unwrap();
      setSyncMatsNote(
        `Добавлено позиций в реестр: ${res.added} (из ТТН: ${res.from_ttn}, из спецификаций РД: ${res.from_rd}).`,
      );
    } catch (e) {
      setSyncMatsNote(apiErrorText(e, 'Не удалось синхронизировать материалы из РД и ТТН.'));
    }
  }

  async function onGenerateFieldAct(reqId: string) {
    setFieldActNote(null);
    try {
      const res = await generateFieldAct({
        id: reqId,
        object_name: header.object_name.trim() || undefined,
      }).unwrap();
      const notes = res.evidence?.evidence_notes?.filter(Boolean).join('; ');
      setFieldActNote(
        res.already
          ? 'Акт АОСР по этой заявке уже был собран ранее.'
          : `АОСР успешно собран из данных стройки, геодезии и ТТН${notes ? ` (${notes})` : ''}.`,
      );
    } catch (e) {
      setFieldActNote(apiErrorText(e, 'Не удалось собрать АОСР по заявке Прораба.'));
    }
  }

  async function onCreateIdPackage(e: React.FormEvent) {
    e.preventDefault();
    const act_ids = Object.entries(pkgActIds)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (!pkgTitle.trim()) return;
    try {
      await createPkg({ projectId, title: pkgTitle.trim(), act_ids }).unwrap();
      setPkgTitle('');
      setPkgActIds({});
    } catch {
      setError('Не удалось создать комплект ИД.');
    }
  }

  async function onUploadTemplate() {
    if (!tplFile) return;
    const form = new FormData();
    form.append('name', tplName.trim() || tplFile.name);
    form.append('kind', kind);
    form.append('file', tplFile);
    try {
      await uploadTpl({ projectId, form }).unwrap();
      setTplName('');
      setTplFile(null);
    } catch {
      setError('Не удалось загрузить шаблон.');
    }
  }

  async function onGenerate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!workName.trim()) {
      setError('Укажите наименование работ.');
      return;
    }
    const parties = [
      { role: 'developer', text: header.developer.trim() },
      { role: 'builder', text: header.builder.trim() },
      { role: 'designer', text: header.designer.trim() },
    ].filter((p) => p.text.length > 0);
    const representatives = reps.filter(aosrRepFilled).map((r) => ({
      role: r.role,
      details: aosrRepDetails(r),
      short_name: r.name.trim() || undefined,
    }));
    const repIds = Object.entries(selectedRepIds)
      .filter(([, v]) => v)
      .map(([k]) => k);
    const matIds = Object.entries(selectedMatIds)
      .filter(([, v]) => v)
      .map(([k]) => k);
    try {
      await generate({
        projectId,
        kind,
        template_id: templateId || undefined,
        form_ref: header.form_ref.trim() || undefined,
        act_no: actNo.trim() || undefined,
        act_date: actDate.trim() || undefined,
        object_name: header.object_name.trim() || undefined,
        object_address: header.object_address.trim() || undefined,
        parties: parties.length ? parties : undefined,
        representatives: representatives.length ? representatives : undefined,
        representative_ids: repIds.length ? repIds : undefined,
        performer: performer.trim() || undefined,
        work_name: workName.trim(),
        rd_docs: rdDocs.trim() || undefined,
        materials: parseMaterials().length ? parseMaterials() : undefined,
        material_ids: matIds.length ? matIds : undefined,
        materials_registry_ref: materialsRegistryRef.trim() || undefined,
        compliance_docs: complianceDocs.trim() || undefined,
        date_start: dateStart.trim() || undefined,
        date_end: dateEnd.trim() || undefined,
        norms: norms.trim() || undefined,
        next_works: nextWorks.trim() || undefined,
        appendices: appendices.trim() || undefined,
      }).unwrap();
      setWorkName('');
      setActNo('');
      setActDate('');
      setMaterialsText('');
      setRdDocs('');
      setNextWorks('');
      setAppendices('');
    } catch {
      setError('Не удалось сгенерировать акт.');
    }
  }

  async function onDownload(id: string, k: string) {
    try {
      const blob = await exportDocx(id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${k}_${id}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Не удалось выгрузить DOCX акта.');
    }
  }

  return (
    <div>
      <Row>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Исполнительная документация</h1>
          <Muted>
            Сквозная сборка АОСР/АОСК/АОУСИТО из заявок Прораба, исполнительных схем, ТТН и сдача в СТДО
          </Muted>
        </div>
        <Link to={`/agents/pto/${projectId}`}>
          <Button $variant="secondary">← К проекту</Button>
        </Link>
      </Row>

      <Columns>
        <div>
          <h3 style={{ marginTop: 0 }}>Новый акт</h3>
          {can_write ? (
            <Panel as="form" onSubmit={onGenerate}>
              <Field>
                Вид акта
                <Select value={kind} onChange={(e) => setKind(e.target.value)}>
                  <option value="aosr">АОСР — освидетельствование скрытых работ</option>
                  <option value="aosk">АОСК — ответственные конструкции</option>
                  <option value="aousito">АОУСИТО — участки сетей ИТО</option>
                </Select>
              </Field>
              <Field>
                Форма акта
                <Select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                  <option value="">Наша форма (РД-11-02-2006 / 344/пр)</option>
                  {actTemplates.map((t) => (
                    <option key={t.id} value={t.id}>
                      Шаблон заказчика: {t.name}
                    </option>
                  ))}
                </Select>
              </Field>

              <Details>
                <summary>Шаблоны заказчика (.docx)</summary>
                <div style={{ marginTop: 12 }}>
                  <Muted style={{ fontSize: 12, marginBottom: 8 }}>
                    Загрузите .docx с плейсхолдерами <code>{'{{act_no}}'}</code>,{' '}
                    <code>{'{{work_name}}'}</code>, <code>{'{{materials}}'}</code>,{' '}
                    <code>{'{{rep_builder}}'}</code>, <code>{'{{name_builder}}'}</code>,{' '}
                    <code>{'{{party_developer}}'}</code> и т.п. — при генерации они заполнятся
                    реквизитами акта.
                  </Muted>
                  {actTemplates.map((t) => (
                    <PosItem key={t.id}>
                      <span style={{ flex: 1 }}>
                        {t.name} <span style={{ opacity: 0.7 }}>({t.kind})</span>
                      </span>
                      <Button
                        $variant="ghost"
                        $size="sm"
                        type="button"
                        onClick={() => deleteTpl(t.id)}
                      >
                        ✕
                      </Button>
                    </PosItem>
                  ))}
                  <Field style={{ marginTop: 10 }}>
                    Название шаблона
                    <Input
                      value={tplName}
                      onChange={(e) => setTplName(e.target.value)}
                      placeholder="АОСР заказчика (СКМ)"
                    />
                  </Field>
                  <input
                    type="file"
                    accept=".docx"
                    onChange={(e) => setTplFile(e.target.files?.[0] ?? null)}
                  />
                  <Button
                    $variant="secondary"
                    $size="sm"
                    type="button"
                    disabled={!tplFile || uploadingTpl}
                    onClick={onUploadTemplate}
                    style={{ marginTop: 8 }}
                  >
                    {uploadingTpl ? 'Загрузка…' : 'Загрузить шаблон'}
                  </Button>
                </div>
              </Details>
              <Field>
                Наименование работ (п.1)
                <Input
                  value={workName}
                  onChange={(e) => setWorkName(e.target.value)}
                  placeholder="Монтаж стеновых сэндвич-панелей в осях…"
                />
              </Field>
              <div style={{ display: 'flex', gap: 8 }}>
                <Field style={{ flex: 1 }}>
                  № акта
                  <Input
                    value={actNo}
                    onChange={(e) => setActNo(e.target.value)}
                    placeholder="СКМ-…"
                  />
                </Field>
                <Field style={{ flex: 1 }}>
                  Дата (ГГГГ-ММ-ДД)
                  <Input
                    value={actDate}
                    onChange={(e) => setActDate(e.target.value)}
                    placeholder="2026-06-06"
                  />
                </Field>
              </div>

              <ReqsBar $tone={reqsStatus.tone}>
                <div>{reqsStatus.text}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {fallbackReqs && (
                    <Button
                      $variant="ghost"
                      $size="sm"
                      type="button"
                      onClick={onAutofillFromFallback}
                    >
                      ⚡ Из Договора и РД
                    </Button>
                  )}
                  <Button
                    $variant="secondary"
                    $size="sm"
                    type="button"
                    disabled={!reqsDirty || savingReqs || !reqQ.isSuccess}
                    onClick={() => void onSaveReqs()}
                  >
                    {savingReqs ? 'Сохраняю…' : 'Сохранить реквизиты проекта'}
                  </Button>
                </div>
              </ReqsBar>
              {legacy && reqQ.isSuccess && !savedForm && (
                <ReqsBar $tone="warn">
                  <div>
                    Реквизиты шапки сохранены только в этом браузере
                    {legacy.object_name ? ` (объект «${legacy.object_name}»)` : ''} — другие
                    инженеры и сборка АОСР по заявкам Прораба их не видят. Сохраните их в проекте:
                    они уже подставлены в форму ниже.
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button
                      $variant="primary"
                      $size="sm"
                      type="button"
                      disabled={savingReqs}
                      onClick={() => void onSaveReqs()}
                    >
                      Сохранить на сервере
                    </Button>
                    <Button $variant="ghost" $size="sm" type="button" onClick={onDropLegacy}>
                      Не переносить
                    </Button>
                  </div>
                </ReqsBar>
              )}
              {reqsNote && (
                <Muted
                  role={reqsNote.ok ? 'status' : 'alert'}
                  style={{ marginBottom: 12, color: reqsNote.ok ? undefined : 'crimson' }}
                >
                  {reqsNote.text}
                </Muted>
              )}

              <Details>
                <summary>Единый паспорт реквизитов объекта (для АОСР, ОЖР и ППР)</summary>
                <div style={{ marginTop: 12 }}>
                  <Muted style={{ fontSize: 12, marginBottom: 10 }}>
                    Единый паспорт реквизитов проекта: синхронизируется между Актами ИД, ОЖР и ППР, а
                    недостающие поля автоматически подтягиваются из Договора Статут и штампов РД.
                  </Muted>
                  <Field>
                    Ссылка на форму
                    <Input
                      value={header.form_ref}
                      onChange={(e) => setHeaderField('form_ref', e.target.value)}
                    />
                  </Field>
                  <Field>
                    Наименование объекта капитального строительства
                    <TextArea
                      value={header.object_name}
                      onChange={(e) => setHeaderField('object_name', e.target.value)}
                      placeholder="«Строительство …». Насосная станция противопожарного водоснабжения"
                    />
                  </Field>
                  <Field>
                    Адрес объекта
                    <Input
                      value={header.object_address}
                      onChange={(e) => setHeaderField('object_address', e.target.value)}
                      placeholder="г. Тобольск, …"
                    />
                  </Field>
                  <Field>
                    Застройщик / технический заказчик
                    <TextArea
                      value={header.developer}
                      onChange={(e) => setHeaderField('developer', e.target.value)}
                      placeholder="ООО «…», ОГРН …, ИНН …, адрес, СРО"
                    />
                  </Field>
                  <Field>
                    Лицо, осуществляющее строительство
                    <TextArea
                      value={header.builder}
                      onChange={(e) => setHeaderField('builder', e.target.value)}
                      placeholder="АО «САТКОМ», ОГРН …, ИНН …, адрес, СРО"
                    />
                  </Field>
                  <Field>
                    Лицо, осуществляющее подготовку ПД
                    <TextArea
                      value={header.designer}
                      onChange={(e) => setHeaderField('designer', e.target.value)}
                      placeholder="АО «…», ОГРН …, ИНН …, адрес, СРО"
                    />
                  </Field>
                </div>
              </Details>

              <Details>
                <summary>Представители (из реестра приказов / вручную)</summary>
                <div style={{ marginTop: 12 }}>
                  <Muted style={{ marginBottom: 6 }}>
                    Из реестра приказов — отметьте для акта:
                  </Muted>
                  {registryReps.length === 0 ? (
                    <Muted style={{ fontSize: 12, marginBottom: 8 }}>
                      Реестр пуст — добавьте ниже.
                    </Muted>
                  ) : (
                    registryReps.map((r) => (
                      <PosItem key={r.id}>
                        <input
                          type="checkbox"
                          checked={!!selectedRepIds[r.id]}
                          onChange={(e) =>
                            setSelectedRepIds((prev) => ({ ...prev, [r.id]: e.target.checked }))
                          }
                        />
                        <span style={{ flex: 1 }}>
                          <b>{repRoleLabel(r.role)}:</b> {r.position ? `${r.position} ` : ''}
                          {r.full_name}
                          {r.order_ref ? ` · ${r.order_ref}` : ''}
                        </span>
                        <Button
                          $variant="ghost"
                          $size="sm"
                          type="button"
                          onClick={() => deleteRep(r.id)}
                        >
                          ✕
                        </Button>
                      </PosItem>
                    ))
                  )}

                  <RepCard style={{ marginTop: 10 }}>
                    <Muted style={{ marginBottom: 6 }}>Добавить в реестр приказов</Muted>
                    <Field>
                      Роль
                      <Select value={nrRole} onChange={(e) => setNrRole(e.target.value)}>
                        {REP_ROLES.map((x) => (
                          <option key={x.value} value={x.value}>
                            {x.label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Field style={{ flex: 2 }}>
                        Должность
                        <Input
                          value={nrPosition}
                          onChange={(e) => setNrPosition(e.target.value)}
                          placeholder="Зам. ген. директора"
                        />
                      </Field>
                      <Field style={{ flex: 1 }}>
                        ФИО
                        <Input
                          value={nrFullName}
                          onChange={(e) => setNrFullName(e.target.value)}
                          placeholder="Мужиков К.О."
                        />
                      </Field>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Field style={{ flex: 1 }}>
                        НРС (опц.)
                        <Input
                          value={nrNrs}
                          onChange={(e) => setNrNrs(e.target.value)}
                          placeholder="С-74-…"
                        />
                      </Field>
                      <Field style={{ flex: 2 }}>
                        Приказ (реквизиты)
                        <Input
                          value={nrOrder}
                          onChange={(e) => setNrOrder(e.target.value)}
                          placeholder="приказ 215/2-П от 07.10.2024"
                        />
                      </Field>
                    </div>
                    <Field>
                      Организация (опц.)
                      <Input
                        value={nrOrg}
                        onChange={(e) => setNrOrg(e.target.value)}
                        placeholder="АО «САТКОМ», ОГРН …"
                      />
                    </Field>
                    <Button
                      $variant="secondary"
                      $size="sm"
                      type="button"
                      disabled={!nrFullName.trim()}
                      onClick={onAddRegistryRep}
                    >
                      + В реестр
                    </Button>
                  </RepCard>

                  <Muted style={{ margin: '12px 0 6px' }}>
                    Представители проекта по умолчанию — сохраняются с реквизитами проекта; ими
                    подписываются и АОСР по заявкам Прораба:
                  </Muted>
                  {reps.map((r, i) => (
                    <RepCard key={i}>
                      <Field>
                        Роль
                        <Select
                          value={r.role}
                          onChange={(e) => updateRep(i, { role: e.target.value })}
                        >
                          {REP_ROLES.map((x) => (
                            <option key={x.value} value={x.value}>
                              {x.label}
                            </option>
                          ))}
                        </Select>
                      </Field>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Field style={{ flex: 2 }}>
                          Должность
                          <Input
                            value={r.position}
                            onChange={(e) => updateRep(i, { position: e.target.value })}
                            placeholder="Заместитель генерального директора по строительству"
                          />
                        </Field>
                        <Field style={{ flex: 1 }}>
                          ФИО (для подписи)
                          <Input
                            value={r.name}
                            onChange={(e) => updateRep(i, { name: e.target.value })}
                            placeholder="Мужиков К.О."
                          />
                        </Field>
                      </div>
                      <Field>
                        Документ о полномочиях: приказ, НРС, организация
                        <TextArea
                          value={r.order_doc}
                          onChange={(e) => updateRep(i, { order_doc: e.target.value })}
                          placeholder="приказ 215/2-П от 07.10.2024 г., АО «САТКОМ»"
                        />
                      </Field>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <Muted style={{ flex: 1, fontSize: 12 }}>
                          {aosrRepFilled(r)
                            ? `В акте: ${aosrRepDetails(r)}`
                            : 'Не заполнен — в акт не попадёт.'}
                        </Muted>
                        <Button
                          $variant="ghost"
                          $size="sm"
                          type="button"
                          onClick={() => removeRep(i)}
                        >
                          Удалить
                        </Button>
                      </div>
                    </RepCard>
                  ))}
                  <Button $variant="secondary" $size="sm" type="button" onClick={addRep}>
                    + Представитель
                  </Button>
                </div>
              </Details>

              <Details>
                <summary>Материалы и документы качества (п.3)</summary>
                <div style={{ marginTop: 12 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      flexWrap: 'wrap',
                      marginBottom: 8,
                    }}
                  >
                    <Muted>Из реестра качества — отметьте для акта:</Muted>
                    <Button
                      $variant="secondary"
                      $size="sm"
                      type="button"
                      disabled={syncingMats}
                      onClick={() => void onSyncMaterialsFromRd()}
                    >
                      {syncingMats ? 'Загрузка…' : '📥 Загрузить из РД и ТТН'}
                    </Button>
                  </div>
                  {syncMatsNote && (
                    <Muted style={{ fontSize: 12, marginBottom: 8 }}>{syncMatsNote}</Muted>
                  )}
                  {registryMaterials.length === 0 ? (
                    <Muted style={{ fontSize: 12, marginBottom: 8 }}>
                      Реестр пуст — нажмите «Загрузить из РД и ТТН» или добавьте ниже.
                    </Muted>
                  ) : (
                    registryMaterials.map((m) => (
                      <PosItem key={m.id}>
                        <input
                          type="checkbox"
                          checked={!!selectedMatIds[m.id]}
                          onChange={(e) =>
                            setSelectedMatIds((prev) => ({ ...prev, [m.id]: e.target.checked }))
                          }
                        />
                        <span style={{ flex: 1 }}>
                          {m.name}
                          {m.quality_docs ? ` — ${m.quality_docs}` : ''}
                        </span>
                        <Button
                          $variant="ghost"
                          $size="sm"
                          type="button"
                          onClick={() => deleteMat(m.id)}
                        >
                          ✕
                        </Button>
                      </PosItem>
                    ))
                  )}
                  <RepCard style={{ marginTop: 10 }}>
                    <Muted style={{ marginBottom: 6 }}>Добавить материал в реестр</Muted>
                    <Field>
                      Наименование материала / изделия
                      <Input
                        value={nmName}
                        onChange={(e) => setNmName(e.target.value)}
                        placeholder="Панель ТСП-Z-150-1000"
                      />
                    </Field>
                    <Field>
                      Документы качества (паспорт/сертификат № + дата)
                      <Input
                        value={nmDocs}
                        onChange={(e) => setNmDocs(e.target.value)}
                        placeholder="паспорт №1972/4 от 16.06.2025"
                      />
                    </Field>
                    <Button
                      $variant="secondary"
                      $size="sm"
                      type="button"
                      disabled={!nmName.trim()}
                      onClick={onAddRegistryMat}
                    >
                      + В реестр
                    </Button>
                  </RepCard>
                  <Muted style={{ margin: '12px 0 6px' }}>Либо вручную для этого акта:</Muted>
                  <Field>
                    Материалы — по одному в строке: «наименование | документы качества»
                    <TextArea
                      value={materialsText}
                      onChange={(e) => setMaterialsText(e.target.value)}
                      placeholder={
                        'Панель ТСП-Z-150-1000 | паспорт №1972/4 от 16.06.2025\nШуруп 6,3х190 | паспорт №363, сертификат РОСС RU'
                      }
                    />
                  </Field>
                  <Field>
                    Либо ссылка на реестр документов качества (если &gt; 5)
                    <Input
                      value={materialsRegistryRef}
                      onChange={(e) => setMaterialsRegistryRef(e.target.value)}
                      placeholder="Реестр документов качества № … к акту"
                    />
                  </Field>
                </div>
              </Details>

              <Details>
                <summary>Прочие разделы (п.2, 4–7)</summary>
                <div style={{ marginTop: 12 }}>
                  <Field>
                    Кто выполнил работы
                    <Input
                      value={performer}
                      onChange={(e) => setPerformer(e.target.value)}
                      placeholder="АО «САТКОМ»"
                    />
                  </Field>
                  <Field>
                    п.2 Проектная документация (чертежи)
                    <TextArea
                      value={rdDocs}
                      onChange={(e) => setRdDocs(e.target.value)}
                      placeholder="PDH2.15383-6013-AR.DW-0002 рев. 4, …"
                    />
                  </Field>
                  <Field>
                    п.4 Документы соответствия (исп. схемы)
                    <TextArea
                      value={complianceDocs}
                      onChange={(e) => setComplianceDocs(e.target.value)}
                    />
                  </Field>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Field style={{ flex: 1 }}>
                      п.5 Дата начала
                      <Input
                        value={dateStart}
                        onChange={(e) => setDateStart(e.target.value)}
                        placeholder="01.09.2025"
                      />
                    </Field>
                    <Field style={{ flex: 1 }}>
                      п.5 Дата окончания
                      <Input
                        value={dateEnd}
                        onChange={(e) => setDateEnd(e.target.value)}
                        placeholder="02.09.2025"
                      />
                    </Field>
                  </div>
                  <Field>
                    п.6 Работы выполнены в соответствии с (НТД)
                    <TextArea
                      value={norms}
                      onChange={(e) => setNorms(e.target.value)}
                      placeholder="СП 70.13330.2012 …"
                    />
                  </Field>
                  <Field>
                    п.7 Разрешается производство последующих работ
                    <TextArea value={nextWorks} onChange={(e) => setNextWorks(e.target.value)} />
                  </Field>
                  <Field>
                    Приложения (исполнительные схемы, чертежи, результаты экспертиз/испытаний)
                    <TextArea value={appendices} onChange={(e) => setAppendices(e.target.value)} />
                  </Field>
                </div>
              </Details>

              <Button $variant="primary" type="submit" disabled={generating}>
                {generating ? 'Генерация…' : 'Сгенерировать акт'}
              </Button>
              {error && <Muted style={{ color: 'crimson', marginTop: 8 }}>{error}</Muted>}
            </Panel>
          ) : (
            <Muted>Недостаточно прав для генерации актов.</Muted>
          )}
        </div>

        <div>
          {inspectionRequests.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ marginTop: 0 }}>
                🏗️ Заявки со стройки на освидетельствование (Прораб → АОСР)
              </h3>
              <Muted style={{ fontSize: 12, marginBottom: 8 }}>
                Мост единого организма: при нажатии «Собрать АОСР в 1 клик» система автоматически
                подтягивает объём из позиции РД, принятые исполнительные геодезические схемы,
                паспорта качества из ТТН и реквизиты проекта.
              </Muted>
              {fieldActNote && (
                <Muted style={{ fontSize: 12, marginBottom: 8 }}>{fieldActNote}</Muted>
              )}
              <Panel>
                {inspectionRequests.map((ir) => {
                  const hasAct = !!ir.executive_act_id && !ir.act_deleted;
                  return (
                    <ActRow key={ir.id}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{ir.title}</div>
                        <Muted style={{ fontSize: 12 }}>
                          Статус: {ir.status}
                          {ir.period_start || ir.period_end
                            ? ` · Период: ${ir.period_start ?? '…'} — ${ir.period_end ?? '…'}`
                            : ''}
                          {hasAct ? ' · ✓ АОСР собран' : ''}
                        </Muted>
                      </div>
                      {can_write && !hasAct && (
                        <Button
                          $variant="secondary"
                          $size="sm"
                          disabled={generatingFieldAct}
                          onClick={() => void onGenerateFieldAct(ir.id)}
                        >
                          ⚡ Собрать АОСР в 1 клик
                        </Button>
                      )}
                    </ActRow>
                  );
                })}
              </Panel>
            </div>
          )}

          <h3 style={{ marginTop: 0 }}>Сгенерированные акты</h3>
          {canDelete && (
            <Muted style={{ marginBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                />
                Показать помеченные на удаление
              </label>
            </Muted>
          )}
          {actsLoading ? (
            <Muted>Загрузка…</Muted>
          ) : acts.length === 0 ? (
            <Muted>Актов пока нет.</Muted>
          ) : (
            <Panel>
              {acts.map((a) => (
                <ActRow key={a.id}>
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {kindLabel[a.kind] ?? a.kind}
                      {a.act_no ? ` № ${a.act_no}` : ''} ·{' '}
                      <span style={{ fontWeight: 400, opacity: 0.85 }}>
                        {statusLabel[a.status] ?? a.status}
                      </span>
                      {a.deleted_at ? (
                        <>
                          {' '}
                          <DeletionBadge>
                            удаляется · осталось {deletionDaysLeft(a.deleted_at)} дн.
                          </DeletionBadge>
                        </>
                      ) : null}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>{a.work_name}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <Button $variant="ghost" $size="sm" onClick={() => onDownload(a.id, a.kind)}>
                      DOCX
                    </Button>
                    {canDelete &&
                      (a.deleted_at ? (
                        <Button $variant="ghost" $size="sm" onClick={() => restoreAct(a.id)}>
                          Восстановить
                        </Button>
                      ) : (
                        <Button
                          $variant="ghost"
                          $size="sm"
                          onClick={() => {
                            if (
                              window.confirm(
                                'Пометить акт на удаление? Будет удалён через 30 дней; до этого можно восстановить.',
                              )
                            )
                              deleteAct(a.id);
                          }}
                        >
                          Удалить
                        </Button>
                      ))}
                    {can_write && !a.deleted_at && a.status === 'draft' && (
                      <Button
                        $variant="ghost"
                        $size="sm"
                        onClick={() => setStatus({ id: a.id, status: 'on_signing' })}
                      >
                        На подпись
                      </Button>
                    )}
                    {can_write && !a.deleted_at && a.status === 'on_signing' && (
                      <Button
                        $variant="ghost"
                        $size="sm"
                        onClick={() => setStatus({ id: a.id, status: 'signed' })}
                      >
                        Подписан
                      </Button>
                    )}
                    {can_write && !a.deleted_at && a.status === 'signed' && (
                      <Button
                        $variant="ghost"
                        $size="sm"
                        onClick={() => setStatus({ id: a.id, status: 'submitted' })}
                      >
                        Сдан
                      </Button>
                    )}
                  </div>
                </ActRow>
              ))}
            </Panel>
          )}

          {/* Встроенная сборка комплектов ИД для сдачи в СТДО */}
          <div style={{ marginTop: 24 }}>
            <h3 style={{ marginTop: 0 }}>📦 Комплекты ИД для сдачи в архив / СТДО</h3>
            {can_write && acts.length > 0 && (
              <Panel as="form" onSubmit={onCreateIdPackage} style={{ marginBottom: 12 }}>
                <Field>
                  Наименование комплекта ИД
                  <Input
                    value={pkgTitle}
                    onChange={(e) => setPkgTitle(e.target.value)}
                    placeholder="Комплект ИД по этапу 1 — фундаменты"
                  />
                </Field>
                <Muted style={{ fontSize: 12, marginBottom: 6 }}>
                  Отметьте акты для включения в комплект:
                </Muted>
                <div style={{ maxHeight: 150, overflowY: 'auto', marginBottom: 10 }}>
                  {acts
                    .filter((a) => !a.deleted_at)
                    .map((a) => (
                      <PosItem key={a.id}>
                        <input
                          type="checkbox"
                          checked={!!pkgActIds[a.id]}
                          onChange={(e) =>
                            setPkgActIds((prev) => ({ ...prev, [a.id]: e.target.checked }))
                          }
                        />
                        <span>
                          {kindLabel[a.kind] ?? a.kind} {a.act_no ? `№ ${a.act_no}` : ''} ·{' '}
                          {a.work_name} ({statusLabel[a.status] ?? a.status})
                        </span>
                      </PosItem>
                    ))}
                </div>
                <Button
                  $variant="secondary"
                  $size="sm"
                  type="submit"
                  disabled={savingPkg || !pkgTitle.trim()}
                >
                  {savingPkg ? 'Сборка…' : '+ Собрать комплект ИД'}
                </Button>
              </Panel>
            )}
            {packages.length === 0 ? (
              <Muted style={{ fontSize: 13 }}>Комплектов ИД пока нет.</Muted>
            ) : (
              <Panel>
                {packages.map((p) => (
                  <ActRow key={p.id}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{p.title}</div>
                      <Muted style={{ fontSize: 12 }}>
                        Актов: {p.act_ids.length} · Статус: {p.status}
                        {p.stdo_ref ? ` · СТДО: ${p.stdo_ref}` : ''}
                      </Muted>
                    </div>
                    {can_write && p.status !== 'accepted' && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Input
                          value={stdoRefs[p.id] ?? p.stdo_ref ?? ''}
                          onChange={(e) =>
                            setStdoRefs((prev) => ({ ...prev, [p.id]: e.target.value }))
                          }
                          placeholder="№ в СТДО"
                          style={{ maxWidth: 120 }}
                        />
                        <Button
                          $variant="ghost"
                          $size="sm"
                          onClick={() =>
                            setPkgStatus({
                              id: p.id,
                              status: 'submitted',
                              stdo_ref: (stdoRefs[p.id] ?? p.stdo_ref) || undefined,
                            })
                          }
                        >
                          В СТДО
                        </Button>
                        <Button
                          $variant="ghost"
                          $size="sm"
                          onClick={() => setPkgStatus({ id: p.id, status: 'accepted' })}
                        >
                          Принят
                        </Button>
                      </div>
                    )}
                  </ActRow>
                ))}
              </Panel>
            )}
          </div>
        </div>
      </Columns>
    </div>
  );
}
